import { Link } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";
import { getMovieById } from "@/utils/movie";

interface FavoritesDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function FavoritesDrawer({ open, onClose }: FavoritesDrawerProps) {
  const { favorites, removeFavorite } = useFavorites();

  if (!open) return null;

  const items = favorites
    .map((id) => getMovieById(id))
    .filter((m): m is NonNullable<typeof m> => Boolean(m));

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-foreground-950/40 animate-fade"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className="relative flex h-full w-full max-w-sm flex-col bg-background-50 shadow-none animate-slide-in">
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <i className="ri-bookmark-3-line text-lg text-primary-600" aria-hidden="true" />
            <h2 className="text-base font-bold text-foreground-950">
              我的收藏
              <span className="ml-2 text-sm font-normal text-foreground-500">{items.length} 部</span>
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭收藏"
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors duration-200 hover:bg-background-200 hover:text-foreground-900"
          >
            <i className="ri-close-line text-lg" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-bookmark-line text-2xl" aria-hidden="true" />
              </div>
              <p className="mt-4 text-sm font-medium text-foreground-900">还没有收藏任何作品</p>
              <p className="mt-1 text-xs text-foreground-500">在电影详情页点击「收藏」，即可保存在这里</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((movie) => (
                <li
                  key={movie.id}
                  className="flex gap-3 rounded-lg border border-background-200 bg-background-100 p-2"
                >
                  <Link to={`/detail/${movie.id}`} onClick={onClose} className="shrink-0">
                    <div className="h-[84px] w-14 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={movie.cover}
                        alt={`${movie.title} 海报`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                  </Link>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <Link
                      to={`/detail/${movie.id}`}
                      onClick={onClose}
                      className="truncate text-sm font-bold text-foreground-950 hover:text-primary-600"
                    >
                      {movie.title}
                    </Link>
                    <span className="mt-0.5 text-xs text-foreground-500">
                      {movie.year} · {movie.genres.slice(0, 2).join(" / ")}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFavorite(movie.id)}
                      className="mt-auto flex w-fit items-center gap-1 text-xs text-primary-600 transition-colors duration-200 hover:text-primary-700"
                    >
                      <i className="ri-delete-bin-6-line" aria-hidden="true" />
                      取消收藏
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-background-200 px-5 py-4 text-xs leading-relaxed text-foreground-500">
          收藏记录保存在本机浏览器中，仅用于演示，不会上传到服务器。
        </div>
      </aside>
    </div>
  );
}